import {
  action,
  makeObservable,
  computed,
  observable,
  runInAction
} from 'mobx';

import {
  IBaseTag,
  IProjectTag,
  mapProjectTagErrorCodeToMessage,
  normalizeProjectTag,
  ProjectTagErrorCode,
  ProjectTagServer
} from 'shared/entities/tags';
import {
  validateIsEmpty,
  validateTextFieldMaxLength,
  ValidatorResult
} from 'shared/entities/validator';
import { IRootStore } from 'shared/entities/store/rootStore';
import { apiUrls } from 'shared/entities/domain';
import { AppNotificationType } from 'shared/entities/appNotifications';

import { LoadingStageModel } from '../loadingStage';

const MAX_TAG_NAME_LENGTH = 128;

export default class ProjectTagModel implements IProjectTag {
  readonly id: string;
  readonly dateCreated: Date;
  readonly usersCount: number;

  readonly updatingStage = new LoadingStageModel();
  readonly deletingStage = new LoadingStageModel();

  private _name: string;
  private _dateUpdated: Date;

  readonly rootStore: IRootStore;

  constructor(tag: IProjectTag, rootStore: IRootStore) {
    this.id = tag.id;
    this.dateCreated = tag.dateCreated;
    this._name = tag.name;
    this.usersCount = tag.usersCount;
    this._dateUpdated = tag.dateUpdated;
    this.rootStore = rootStore;

    makeObservable<ProjectTagModel, '_name' | '_dateUpdated'>(this, {
      _name: observable,
      _dateUpdated: observable,
      usersCount: observable,

      name: computed,

      applyFromServer: action
    });
  }

  get name(): string {
    return this._name.trim();
  }

  get dateUpdated(): Date {
    return this._dateUpdated;
  }

  applyFromServer(raw: ProjectTagServer): void {
    const normalized = normalizeProjectTag(raw);

    this._name = normalized.name;
    this._dateUpdated = normalized.dateUpdated;
  }

  async update(
    name: string
  ): Promise<
    BaseResponse<
      ProjectTagModel | null,
      { errorCode: ProjectTagErrorCode | null }
    >
  > {
    const trimmed = name.trim();
    const isError = ProjectTagModel.validateName(trimmed);

    if (isError || this.updatingStage.isLoading) {
      return {
        isError: true,
        data: { errorCode: null }
      };
    }

    if (trimmed === this.name) {
      return {
        isError: false,
        data: this
      };
    }

    this.updatingStage.loading();

    const response = await this.rootStore.networkStore.api<{
      tag: ProjectTagServer;
    }>(apiUrls.USER_TAGS_UPDATE, {
      method: 'POST',
      data: { tag_id: this.id, name: trimmed },
      errorsMap: mapProjectTagErrorCodeToMessage,
      showExpectedError: false
    });

    if (response.isError) {
      this.updatingStage.error();

      const errorCode = response.data?.code as ProjectTagErrorCode | undefined;

      if (
        errorCode !== ProjectTagErrorCode.tagNotFound &&
        errorCode !== ProjectTagErrorCode.tagAlreadyExists
      ) {
        this.rootStore.appNotificationsStore.open({
          type: AppNotificationType.error,
          title: (t) =>
            t('projectTags.notifications.updateError', {
              ns: 'usersPage',
              name: this.name
            }),
          highlightedParts: [this.name]
        });
      }

      return {
        isError: true,
        data: { errorCode: errorCode ?? null }
      };
    }

    runInAction(() => {
      this.applyFromServer(response.data.tag);
      this.updatingStage.success();
    });

    this.rootStore.appNotificationsStore.open({
      type: AppNotificationType.success,
      title: (t) =>
        t('projectTags.notifications.updateSuccess', {
          ns: 'usersPage',
          name: this.name
        }),
      highlightedParts: [this.name]
    });

    return {
      isError: false,
      data: this
    };
  }

  async delete(): Promise<
    BaseResponse<null, { errorCode: ProjectTagErrorCode | null }>
  > {
    if (this.deletingStage.isLoading) {
      return {
        isError: true,
        data: { errorCode: null }
      };
    }

    const tagName = this.name;

    this.deletingStage.loading();

    const response = await this.rootStore.networkStore.api<
      Record<string, never>,
      ProjectTagErrorCode
    >(apiUrls.USER_TAGS_DELETE, {
      method: 'POST',
      data: { tag_id: this.id },
      errorsMap: mapProjectTagErrorCodeToMessage,
      expectedErrorCodes: [ProjectTagErrorCode.tagNotFound],
      showExpectedError: false,
      showUnexpectedError: false
    });

    if (response.isError) {
      this.deletingStage.error();
      const errorCode = response.data?.code as ProjectTagErrorCode | undefined;

      if (errorCode === ProjectTagErrorCode.tagNotFound) {
        return {
          isError: false,
          data: null
        };
      }

      if (
        errorCode !== ProjectTagErrorCode.tagAlreadyExists &&
        errorCode !== ProjectTagErrorCode.tagNotFound
      ) {
        this.rootStore.appNotificationsStore.open({
          type: AppNotificationType.error,
          title: (t) =>
            t('projectTags.notifications.deleteError', {
              ns: 'usersPage',
              name: tagName
            }),
          highlightedParts: [tagName]
        });
      }

      return {
        isError: true,
        data: { errorCode: errorCode ?? null }
      };
    }

    this.deletingStage.success();

    this.rootStore.appNotificationsStore.open({
      type: AppNotificationType.success,
      title: (t) =>
        t('projectTags.notifications.deleteSuccess', {
          ns: 'usersPage',
          name: tagName
        }),
      highlightedParts: [tagName]
    });

    return {
      isError: false,
      data: null
    };
  }

  static validateName(value: string): ValidatorResult {
    const emptyError = validateIsEmpty(value);
    if (emptyError) {
      return emptyError;
    }

    return validateTextFieldMaxLength(value, MAX_TAG_NAME_LENGTH);
  }

  static isDuplicateName(
    tags: IBaseTag[],
    name: string,
    excludeId?: string
  ): boolean {
    const trimmed = name.trim();

    if (!trimmed) {
      return false;
    }

    return tags.some(
      (tag) => tag.id !== excludeId && tag.name.trim() === trimmed
    );
  }

  static fromJson(
    raw: ProjectTagServer,
    rootStore: IRootStore
  ): ProjectTagModel {
    return new ProjectTagModel(normalizeProjectTag(raw), rootStore);
  }

  static toListItem(
    raw: ProjectTagServer,
    rootStore: IRootStore
  ): {
    entity: ProjectTagModel;
    key: string;
  } {
    return {
      entity: ProjectTagModel.fromJson(raw, rootStore),
      key: raw._id
    };
  }
}
